import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Crown, BookOpen, Lightbulb, FileBox, MessageCircle, Send,
  ChevronRight, Play, ExternalLink, Lock, Download, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import "../portfolio/styles.css";

const API = process.env.REACT_APP_BACKEND_URL;

async function api(method, path, body) {
  const opts = { method, headers: { "Content-Type": "application/json" }, credentials: "include" };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API}/api/premium${path}`, opts);
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Error ${r.status}`); }
  return r.json();
}

const SECTIONS = [
  { id: "tutorials", label: "Tutorials", icon: BookOpen, desc: "Custom-made tutorials with tried & tested methods" },
  { id: "tips", label: "Exclusive Tips", icon: Lightbulb, desc: "Insider tips on content creation & AI marketing" },
  { id: "templates", label: "Template Library", icon: FileBox, desc: "Ready-to-use templates for your projects" },
  { id: "chat", label: "Direct Chat with Rosh", icon: MessageCircle, desc: "1:1 support and guidance" },
];

function ContentCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="tribal-card overflow-hidden" style={{ marginBottom: "20px" }} data-testid={`premium-content-${item.id}`}>
      <div className="p-6">
        <div className="flex items-start gap-3 mb-3">
          {item.type === "tutorial" && <BookOpen size={18} className="mt-0.5 shrink-0 text-[#B5793F]" />}
          {item.type === "tip" && <Lightbulb size={18} className="mt-0.5 shrink-0 text-[#C6A97A]" />}
          {item.type === "update" && <Sparkles size={18} className="mt-0.5 shrink-0 text-[#B5793F]" />}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-[#F1E7D1]">{item.title}</h3>
            {item.description && <p className="text-sm tribal-text mt-1">{item.description}</p>}
          </div>
        </div>
        {item.body && (
          <>
            <div className={`text-sm tribal-text leading-relaxed whitespace-pre-line ${expanded ? "" : "line-clamp-4"}`}>
              {item.body}
            </div>
            {item.body.length > 200 && (
              <button onClick={() => setExpanded(!expanded)} className="text-xs font-medium mt-2 text-[#B5793F] hover:underline" data-testid={`content-expand-${item.id}`}>
                {expanded ? "Show less" : "Read more"}
              </button>
            )}
          </>
        )}
        {item.media_url && (
          <div className="mt-4">
            {item.media_url.includes("youtube.com") || item.media_url.includes("youtu.be") ? (
              <div className="aspect-video rounded-lg overflow-hidden bg-black">
                <iframe src={item.media_url.replace("watch?v=", "embed/")} className="w-full h-full" frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={item.title} />
              </div>
            ) : (
              <a href={item.media_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#B5793F] hover:underline">
                <ExternalLink size={14} /> View resource
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateCard({ item }) {
  return (
    <div className="tribal-card p-5" data-testid={`premium-template-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(181,121,63,0.15)" }}>
          <FileBox size={18} className="text-[#B5793F]" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-[#F1E7D1] truncate">{item.title}</h4>
          {item.category && <span className="text-[10px] uppercase tracking-wider text-[#C6A97A] font-semibold">{item.category}</span>}
          {item.description && <p className="text-xs tribal-text mt-1 line-clamp-2">{item.description}</p>}
        </div>
      </div>
      {item.file_url && (
        <a href={item.file_url} target="_blank" rel="noreferrer" data-testid={`template-download-${item.id}`}
           className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border border-[#B5793F] text-[#B5793F] hover:bg-[#B5793F] hover:text-[#F1E7D1] transition-colors">
          <Download size={12} /> Download
        </a>
      )}
    </div>
  );
}

function DirectChat() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => {
    api("GET", "/messages").then(setMessages).catch(() => {});
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api("POST", "/messages", { text: text.trim() });
      setText("");
      load();
    } catch (err) { toast.error(err.message); }
    setSending(false);
  };

  return (
    <div className="tribal-card overflow-hidden flex flex-col" style={{ height: "500px", borderRadius: "12px" }} data-testid="premium-chat">
      <div className="px-5 py-4 border-b border-[#B5793F20] flex items-center gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #8D6E63 0%, #5C4033 100%)" }}>
          <span className="text-[#F1E7D1] text-xs font-bold">R</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#F1E7D1]">Rosh</p>
          <p className="text-[11px] tribal-muted">Direct support — your premium channel</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <MessageCircle size={28} className="mx-auto mb-3 text-[#B5793F]" />
            <p className="text-sm tribal-text">Start a conversation with Rosh</p>
            <p className="text-xs tribal-muted mt-1">Ask questions, share progress, get personalized guidance</p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender === "member" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${msg.id}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
              msg.sender === "member"
                ? "text-[#F1E7D1]"
                : "text-[#A69888] border border-[#B5793F20]"}`}
              style={msg.sender === "member" ? { background: "#8D6E63" } : { background: "rgba(42,29,18,0.5)" }}>
              <p className="text-sm whitespace-pre-line">{msg.text}</p>
              <p className="text-[10px] mt-1 tribal-muted">
                {new Date(msg.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="p-4 border-t border-[#B5793F20] flex gap-3" data-testid="chat-input-form">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your message..."
               className="flex-1 bg-transparent outline-none text-sm text-[#F1E7D1] placeholder-[#8A7E6B] px-3"
               data-testid="chat-input" disabled={sending} />
        <button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-btn"
                className="tribal-btn-primary w-10 h-10 rounded-full flex items-center justify-center !p-0 disabled:opacity-40">
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}

export default function PremiumPortal({ user }) {
  const [section, setSection] = useState("tutorials");
  const [content, setContent] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api("GET", "/status").then(setStatus).catch(() => setStatus({ is_premium: false }));
  }, []);

  useEffect(() => {
    if (!status?.is_premium) return;
    if (section === "tutorials") {
      api("GET", "/content?content_type=tutorial").then(setContent).catch(() => setContent([]));
    } else if (section === "tips") {
      api("GET", "/content?content_type=tip").then(setContent).catch(() => setContent([]));
    } else if (section === "templates") {
      const q = catFilter ? `/templates?category=${encodeURIComponent(catFilter)}` : "/templates";
      api("GET", q).then(setTemplates).catch(() => setTemplates([]));
      api("GET", "/template-categories").then(setCategories).catch(() => setCategories([]));
    }
  }, [section, status, catFilter]);

  if (!status) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-[#B5793F18] rounded-xl animate-pulse" />
        <div className="h-4 w-96 bg-[#B5793F10] rounded-full animate-pulse" />
        <div className="h-64 tribal-surface rounded-lg animate-pulse mt-6" />
      </div>
    );
  }

  /* ---------- Locked state for non-premium users ---------- */
  if (!status.is_premium) {
    return (
      <div className="relative overflow-hidden" data-testid="premium-locked">
        {/* Warli illustration background */}
        <img src="/warli-art/tree-of-life.jpg" alt="" className="absolute top-0 right-0 w-64 md:w-96 opacity-[0.07] pointer-events-none select-none" style={{ filter: "brightness(1.6)" }} />

        <div className="tribal-card p-10 md:p-14 text-center relative z-10">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #8D6E63 0%, #5C4033 100%)" }}>
            <Crown size={28} className="text-[#C6A97A]" />
          </div>
          <h2 className="tribal-title text-3xl mb-3">Premium Access</h2>
          <p className="text-sm tribal-text max-w-md mx-auto mb-6">
            Get exclusive tutorials, templates, direct chat with Rosh, and insider tips on content creation and AI-powered marketing.
          </p>

          <div className="tribal-divider mb-8 max-w-xs mx-auto" />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg mx-auto mb-8">
            {SECTIONS.map((s) => (
              <div key={s.id} className="text-center">
                <s.icon size={20} className="mx-auto mb-1.5 text-[#B5793F]" />
                <p className="text-[11px] font-medium tribal-text">{s.label}</p>
              </div>
            ))}
          </div>
          <a href="/services" data-testid="premium-cta"
             className="tribal-btn-primary inline-flex items-center gap-2">
            <Crown size={15} /> Learn More & Get Access
          </a>
        </div>
      </div>
    );
  }

  /* ---------- Premium portal content ---------- */
  return (
    <div data-testid="premium-portal">
      {/* Header with Warli art accent */}
      <div className="flex items-center gap-4 mb-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #8D6E63 0%, #5C4033 100%)" }}>
          <Crown size={18} className="text-[#C6A97A]" />
        </div>
        <div>
          <h2 className="tribal-title text-2xl md:text-3xl">Premium Portal</h2>
          <p className="text-xs tribal-muted">
            Your exclusive space — tutorials, tips, templates & direct support
            {status.premium_expires_at && (
              <span className="ml-2">· Access until {new Date(status.premium_expires_at).toLocaleDateString()}</span>
            )}
          </p>
        </div>
      </div>

      {/* Warli dancers divider */}
      <div className="tribal-dancers mb-3" />
      <div className="tribal-divider mb-8" />

      {/* Section Nav — tribal pill buttons */}
      <nav className="flex gap-3 mb-8 overflow-x-auto pb-2 scrollbar-hide" data-testid="premium-sections" style={{ WebkitOverflowScrolling: "touch" }}>
        {SECTIONS.map((s) => {
          const active = section === s.id;
          return (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`premium-section-${s.id}`}
                    className="shrink-0 px-5 py-3 md:px-6 md:py-4 rounded-full flex items-center gap-2.5 transition-all hover:-translate-y-0.5"
                    style={{
                      background: active ? "#8D6E63" : "rgba(42,29,18,0.6)",
                      color: active ? "#F1E7D1" : "#A69888",
                      border: "1px solid " + (active ? "#B5793F" : "rgba(181,121,63,0.2)"),
                      boxShadow: active ? "0 10px 26px rgba(0,0,0,0.4), 0 0 10px rgba(181,121,63,0.15)" : "none",
                    }}>
              <s.icon size={16} style={{ color: "#B5793F" }} />
              <span className="text-sm font-semibold whitespace-nowrap" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{s.label}</span>
              {s.id === "chat" && status.unread_messages > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{status.unread_messages}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Content — Tutorials */}
      {section === "tutorials" && (
        <div data-testid="premium-tutorials">
          {content.length === 0 ? (
            <div className="tribal-card p-10 text-center relative overflow-hidden">
              <img src="/warli-art/drummers.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.05] pointer-events-none" style={{ filter: "brightness(1.5)" }} />
              <div className="relative z-10">
                <BookOpen size={24} className="mx-auto mb-3 text-[#B5793F]" />
                <p className="tribal-heading rp-italic text-base md:text-lg text-[#8A7E6B]">Premium tutorials coming soon — stay tuned!</p>
                <div className="tribal-divider mt-6 max-w-xs mx-auto" />
              </div>
            </div>
          ) : content.map((item) => <ContentCard key={item.id} item={item} />)}
        </div>
      )}

      {/* Content — Tips */}
      {section === "tips" && (
        <div data-testid="premium-tips">
          {content.length === 0 ? (
            <div className="tribal-card p-10 text-center relative overflow-hidden">
              <img src="/warli-art/village-scene.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.05] pointer-events-none" style={{ filter: "brightness(1.5)" }} />
              <div className="relative z-10">
                <Lightbulb size={24} className="mx-auto mb-3 text-[#C6A97A]" />
                <p className="tribal-heading rp-italic text-base md:text-lg text-[#8A7E6B]">Exclusive tips coming soon!</p>
                <div className="tribal-divider mt-6 max-w-xs mx-auto" />
              </div>
            </div>
          ) : content.map((item) => <ContentCard key={item.id} item={item} />)}
        </div>
      )}

      {/* Content — Templates */}
      {section === "templates" && (
        <div data-testid="premium-templates">
          {categories.length > 0 && (
            <div className="flex gap-2 mb-5 flex-wrap">
              <button onClick={() => setCatFilter("")}
                      className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all"
                      style={{
                        background: !catFilter ? "#8D6E63" : "rgba(42,29,18,0.6)",
                        color: !catFilter ? "#F1E7D1" : "#A69888",
                        border: "1px solid " + (!catFilter ? "#B5793F" : "rgba(181,121,63,0.2)"),
                      }}>
                All
              </button>
              {categories.map((c) => (
                <button key={c} onClick={() => setCatFilter(c)}
                        className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all"
                        style={{
                          background: catFilter === c ? "#8D6E63" : "rgba(42,29,18,0.6)",
                          color: catFilter === c ? "#F1E7D1" : "#A69888",
                          border: "1px solid " + (catFilter === c ? "#B5793F" : "rgba(181,121,63,0.2)"),
                        }}>
                  {c}
                </button>
              ))}
            </div>
          )}
          {templates.length === 0 ? (
            <div className="tribal-card p-10 text-center relative overflow-hidden">
              <img src="/warli-art/dancers.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.05] pointer-events-none" style={{ filter: "brightness(1.5)" }} />
              <div className="relative z-10">
                <FileBox size={24} className="mx-auto mb-3 text-[#B5793F]" />
                <p className="tribal-heading rp-italic text-base md:text-lg text-[#8A7E6B]">Template library coming soon!</p>
                <div className="tribal-divider mt-6 max-w-xs mx-auto" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {templates.map((t) => <TemplateCard key={t.id} item={t} />)}
            </div>
          )}
        </div>
      )}

      {section === "chat" && <DirectChat />}
    </div>
  );
}
